import React from 'react';

import Portal from '@src/components/ui2/Portal';
import useTranslate from '@src/i18n/useTranslate';
import { IconBrandWhatsapp, IconBuildingStore, IconClock, IconX } from '@planne-software/uni/icons';
import PlanneConciergeLogo from '@src/assets/images/planne-concierge-logo-white.svg';

import {
  DeclineButton,
  ModalBody,
  ModalCloseButton,
  ModalDivider,
  ModalHero,
  ModalHeroGlow,
  ModalHintIcon,
  ModalHintRow,
  ModalHintText,
  ModalLogo,
  ModalOverlay,
  ModalPanel,
  ModalTitle,
  WhatsappButton,
} from './styles';
import { useCashbackGrowthModal } from './useCashbackGrowthModal';

export type CashbackGrowthModalProps = {
  open: boolean;
  orderCode: string;
  amountCents: number;
  buyerFirstName?: string;
  onDismiss: () => void;
};

export const CashbackGrowthModal = (props: CashbackGrowthModalProps) => {
  const { open, orderCode, amountCents, buyerFirstName, onDismiss } = props;
  const { i18n, i18nElement } = useTranslate();
  const { panelRef, titleRef, handleRedeem, handleDismiss } = useCashbackGrowthModal({
    open,
    orderCode,
    amountCents,
    buyerFirstName,
    onDismiss,
  });

  if (!open) {
    return null;
  }

  return (
    <Portal>
      <ModalOverlay
        role='presentation'
        onClick={(event) => {
          if (event.target === event.currentTarget) {
            handleDismiss();
          }
        }}
      >
        <ModalPanel ref={panelRef} data-testid='cashback-growth-modal' data-confetti-origin='true'>
          <ModalHero>
            <ModalHeroGlow aria-hidden />
            <ModalCloseButton type='button' aria-label={i18n('base_cancel')} onClick={handleDismiss}>
              <IconX size={14} aria-hidden />
            </ModalCloseButton>
            <ModalLogo>
              <PlanneConciergeLogo role='img' aria-label='Planne Concierge' />
            </ModalLogo>
            <ModalTitle ref={titleRef}>{i18n('cashback_growth_modal_title')}</ModalTitle>
          </ModalHero>

          <ModalDivider aria-hidden />

          <ModalBody>
            <ModalHintRow>
              <ModalHintIcon $variant='primary'>
                <IconBuildingStore size={20} aria-hidden />
              </ModalHintIcon>
              <ModalHintText>{i18nElement('cashback_growth_modal_store_hint')}</ModalHintText>
            </ModalHintRow>
            <ModalHintRow>
              <ModalHintIcon $variant='warning'>
                <IconClock size={20} aria-hidden />
              </ModalHintIcon>
              <ModalHintText $coloredHighlight>{i18nElement('cashback_growth_modal_deadline')}</ModalHintText>
            </ModalHintRow>
            <WhatsappButton type='button' onClick={handleRedeem}>
              <IconBrandWhatsapp size={22} aria-hidden />
              {i18n('cashback_growth_redeem_whatsapp')}
            </WhatsappButton>
            <DeclineButton type='button' onClick={handleDismiss}>
              {i18n('cashback_growth_decline')}
            </DeclineButton>
          </ModalBody>
        </ModalPanel>
      </ModalOverlay>
    </Portal>
  );
};

export default CashbackGrowthModal;
